import { useMemo, useCallback, useRef, useEffect } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { getPubSubClient, eventToAvi } from '@confluence/pubsub-client';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { getEditorResolvedState } from '@confluence/comments-util/entry-points/stateResolver';
import { getSortedMarkerRefs } from '@confluence/comments-util';
import { useAnnotations } from '@confluence/annotation-provider-store';
import { useCommentsData, CommentActionType, CommentType } from '@confluence/comments-data';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data';

import { InlineCommentPubSubEvents } from '../CommentPubSubEvents';
import {
	refreshCommentThread,
	handleAddedReplyInCommentThread,
	handleAddNewCommentThread,
	handleEditedComment,
} from '../utils/pubsubUtils';

type BasePubsubProps = {
	contentId: string;
	contentType?: string | null;
};

export type PubSubProps = BasePubsubProps & {
	triggerAnalyticsEvent?:
		| ((
				eventName: string,
				extraData?: {
					[key: string]: any;
				},
		  ) => Promise<void>)
		| CreateUIAnalyticsEvent;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
};

export const useEditorInlineCommentsPubSub = ({
	contentId,
	contentType,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
}: PubSubProps) => {
	const pubSubResourceType = contentType || 'page';

	const [
		{ orderedActiveAnnotationIdList },
		{
			addReplyToCommentThread,
			addNewCommentThreads,
			updateRemovedCommentIdsMap,
			updateCommentCount,
			updateCommentBody,
		},
	] = useCommentsData();
	const { userId: currentUserId } = useSessionData();
	const orderedActiveAnnotationIdListRef = useRef(orderedActiveAnnotationIdList);
	const { editorViewProps } = useAnnotations();

	const { annotations = [] } = editorViewProps || {};
	const selectedAnnotationId = annotations[0]?.id;

	useEffect(() => {
		orderedActiveAnnotationIdListRef.current = orderedActiveAnnotationIdList;
	}, [orderedActiveAnnotationIdList]);

	const handleOnPublicationEvent = useCallback(
		(event: string, markerRef: any) => {
			const emitter = getEditorAnnotationEventEmitter();
			emitter.emit(event, markerRef);
			const annotationIds = orderedActiveAnnotationIdListRef.current.map(
				(annotation) => annotation.threadKey,
			);

			if (triggerAnalyticsEvent) {
				// Updates ordered list of unresolved inline comments based on ADF to account for real-time changes (collaborative editing)
				void getEditorResolvedState({
					// the annotations won't already include the pubsub marker ref
					annotations: getSortedMarkerRefs([...annotationIds, markerRef], 'edit'),
					pageId: contentId,
					triggerAnalyticsEvent,
					updateUnresolvedInlineComment,
				});
			}
		},
		[contentId, triggerAnalyticsEvent, updateUnresolvedInlineComment],
	);

	const onPublicationEvent = useCallback(
		async (event: string, payload: any) => {
			const { inlineCommentRequestBody, accountId } = payload;
			const { markerRef, commentId, inlineCommentType } = inlineCommentRequestBody;

			// Don't do anything when the current user performs an action
			if (accountId === currentUserId) {
				return;
			}

			const isReply = inlineCommentType === 'REPLY';

			switch (event) {
				case InlineCommentPubSubEvents.InlineCommentUnresolved:
					if (fg('confluence-frontend-comments-panel')) {
						// We load resolved + active separately, so unless the user has visited the resolved tab, the comment thread won't be in the state
						// Hance the await here - as part of this function we update state
						await handleAddNewCommentThread({
							contentId,
							markerRef,
							addNewCommentThreads,
						});

						updateCommentCount &&
							updateCommentCount({
								threadKey: markerRef,
								actionType: CommentActionType.REOPEN_COMMENT_THREAD,
								commentType: CommentType.INLINE,
							});
					}
					handleOnPublicationEvent('unresolve', markerRef);
					break;
				case InlineCommentPubSubEvents.InlineCommentResolved:
					handleOnPublicationEvent('resolve', markerRef);

					if (fg('confluence-frontend-comments-panel')) {
						const { inlineResolveProperties, inlineText } = inlineCommentRequestBody;

						updateCommentCount &&
							updateCommentCount({
								threadKey: markerRef,
								actionType: CommentActionType.RESOLVE_COMMENT_THREAD,
								commentType: CommentType.INLINE,
							});

						if (inlineResolveProperties && inlineText) {
							updateRemovedCommentIdsMap({
								threadKey: markerRef,
								commentId,
								commentActionType: CommentActionType.RESOLVE_COMMENT_THREAD,
								isReply,
								resolvedProperties: {
									inlineResolveProperties,
									inlineText,
								},
								commentType: CommentType.INLINE,
							});
						}
					}

					break;
				case InlineCommentPubSubEvents.InlineCommentCreated:
				case InlineCommentPubSubEvents.EditorInlineCommentCreated:
					if (fg('confluence-frontend-comments-panel')) {
						if (isReply) {
							void handleAddedReplyInCommentThread({
								contentId,
								markerRef,
								commentId,
								addReplyToCommentThread,
								commentType: CommentType.INLINE,
							});
						} else {
							void handleAddNewCommentThread({
								contentId,
								markerRef,
								addNewCommentThreads,
							});
						}
						updateCommentCount &&
							updateCommentCount({
								threadKey: markerRef,
								actionType: CommentActionType.CREATE_COMMENT,
								commentType: CommentType.INLINE,
							});
					} else {
						if (isReply && markerRef === selectedAnnotationId) {
							void refreshCommentThread(contentId, markerRef);
						}
					}

					handleOnPublicationEvent('create', markerRef);
					break;
				case InlineCommentPubSubEvents.InlineCommentDeleted:
					// both a parent comment and reply can be deleted
					if (fg('confluence-frontend-comments-panel')) {
						updateCommentCount &&
							updateCommentCount({
								threadKey: markerRef,
								actionType: CommentActionType.DELETE_COMMENT,
								commentType: CommentType.INLINE,
							});

						updateRemovedCommentIdsMap({
							threadKey: markerRef,
							commentId,
							isReply,
							commentActionType: CommentActionType.DELETE_COMMENT,
							commentType: CommentType.INLINE,
						});
					}
					// We update useCommentsData state for the full panel, but the standalone comment doesn't look at it, so keep in sync via the API
					if (isReply && markerRef === selectedAnnotationId) {
						void refreshCommentThread(contentId, markerRef);
					}
					break;
				case InlineCommentPubSubEvents.InlineCommentUpdated:
					if (fg('confluence-frontend-comments-panel')) {
						void handleEditedComment({
							contentId,
							markerRef,
							commentId,
							updateCommentBody,
						});
					} else {
						if (markerRef === selectedAnnotationId) {
							void refreshCommentThread(contentId, markerRef);
						}
					}
					break;
				default:
					break;
			}
		},
		[
			contentId,
			handleOnPublicationEvent,
			selectedAnnotationId,
			addNewCommentThreads,
			addReplyToCommentThread,
			updateRemovedCommentIdsMap,
			currentUserId,
			updateCommentCount,
			updateCommentBody,
		],
	);

	const register = useCallback(() => {
		void getPubSubClient().then((client) => {
			client
				.joinChannel(pubSubResourceType, contentId)
				.finally(() => {
					void client.subscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

					void client.subscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

					void client.subscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

					void client.subscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);

					void client.subscribe(eventToAvi('updated:inline_comment'), onPublicationEvent);

					if (fg('confluence-frontend-comments-panel')) {
						void client.subscribe(eventToAvi('deleted:inline_comment'), onPublicationEvent);
					}
				})
				.catch((error) => {
					throw new Error(`PubSub Web Client joinChannel ${JSON.stringify(error)}`);
				});
		});
	}, [contentId, onPublicationEvent, pubSubResourceType]);

	const unregister = useCallback(() => {
		void getPubSubClient().then((client) => {
			void client.leaveChannel(pubSubResourceType, contentId);
			void client.unsubscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

			void client.unsubscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

			void client.unsubscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

			void client.unsubscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);

			void client.unsubscribe(eventToAvi('updated:inline_comment'), onPublicationEvent);

			if (fg('confluence-frontend-comments-panel')) {
				void client.unsubscribe(eventToAvi('deleted:inline_comment'), onPublicationEvent);
			}
		});
	}, [contentId, onPublicationEvent, pubSubResourceType]);

	return useMemo(
		() => ({
			register,
			unregister,
			onPublicationEvent,
		}),
		[register, unregister, onPublicationEvent],
	);
};
